import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormRecord,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { GetAnswerRequest } from '../../../../../../backend/src/core/openai/openai.gate';
import { Company } from '../../../core/company/company';
import { companyCategoryList } from '../../../core/company/company-category';
import { CompanyService } from '../../../core/company/company.service';
import { OpenaiModel } from '../../../core/openai/openai';
import { OpenaiService } from '../../../core/openai/openai.service';
import { getIndustryFunctions, getIndustryPrompt } from '../../../core/prompt/company/get-industry.prompt';

@Component({
  selector: 'app-new-edit-company',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzListModule,
    NzSelectModule,
    NzPopconfirmModule,
    NzAutocompleteModule,
  ],
  templateUrl: './new-edit-company.component.html',
  styleUrl: './new-edit-company.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewEditCompanyComponent {
  @Input() selectedItem?: Company;
  @Input() isNewEditVisible = false;

  @Output() isNewEditVisibleChange = new EventEmitter<boolean>();

  validateForm: FormRecord<FormControl<string>>;

  companyCategoryFilteredOptions: string[] = companyCategoryList;
  companyCategoryOptions = companyCategoryList;
  onCompanyCategoryChange(value: string) {
    this.companyCategoryFilteredOptions = this.companyCategoryOptions.filter(
      (option) => option.toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }

  constructor(
    private messageService: NzMessageService,
    private companyService: CompanyService,
    private openaiService: OpenaiService,
    private fb: NonNullableFormBuilder
  ) {
    this.validateForm = this.fb.group({
      title: ['', [Validators.required]],
      description: [''],
      websiteUrl: [''],
      companyType: [''],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      const chng = changes[propName];
      if (propName === 'selectedItem') {
        if (chng.previousValue !== chng.currentValue) {
          const selectedItem = chng.currentValue;
          this.validateForm.patchValue({
            title: selectedItem.title ?? '',
            description: selectedItem.description ?? '',
            websiteUrl: selectedItem.unsafeSubData?.websiteUrl ?? '',
            companyType: selectedItem.type ?? '',
          });
        }
      }
    }

    if (!this.selectedItem) {
      this.validateForm = this.fb.group({
        title: ['', [Validators.required]],
        description: [''],
        websiteUrl: [''],
        companyType: [''],
      });
    }
  }

  isGetIndustryLoading = signal<boolean>(false);
  onGetIndustryClick() {
    const title = this.validateForm.value['title'];
    const description = this.validateForm.value['description'];

    if (!title) {
      this.messageService.error('Please enter a company name.');
      return;
    }

    this.isGetIndustryLoading.set(true);

    const request: GetAnswerRequest = {
      messageList: [
        {
          role: 'user',
          content: getIndustryPrompt(title, description),
        },
      ],
      config: {
        openaiModel: OpenaiModel.gpt4,
        functions: getIndustryFunctions(),
      },
    };

    this.openaiService
      .getAnswer$(request)
      .pipe(take(1))
      .subscribe((response) => {
        this.isGetIndustryLoading.set(false);
        this.validateForm.patchValue({
          companyType: response?.industryCategory,
        });
      });
  }

  onIsNewEditVisibleChange(event: boolean) {
    this.isNewEditVisibleChange.emit(event);
  }

  onCancelClick() {
    this.isNewEditVisible = false;

    this.isNewEditVisibleChange.emit(false);
  }

  onDeleteClick() {
    if (this.selectedItem) {
      this.companyService.delete$([this.selectedItem!]).pipe(take(1)).subscribe();
    }

    this.isNewEditVisible = false;
    this.isNewEditVisibleChange.emit(this.isNewEditVisible);
  }

  submitForm() {
    if (this.validateForm.valid) {
      this.isNewEditVisible = false;
      this.isNewEditVisibleChange.emit(false);

      const title = this.validateForm.value['title'] ?? 'Default Company';
      const description = this.validateForm.value['description'] ?? 'Default Description';
      const websiteUrl = this.validateForm.value['websiteUrl'] ?? '';
      const companyType = this.validateForm.value['companyType'] ?? '';

      if (this.selectedItem) {
        this.selectedItem.title = title;
        this.selectedItem.description = description;
        this.selectedItem.type = companyType;

        if (!this.selectedItem.unsafeSubData) {
          this.selectedItem.unsafeSubData = {};
        }

        this.selectedItem.unsafeSubData.websiteUrl = websiteUrl;

        this.companyService.update$(this.selectedItem!).pipe(take(1)).subscribe();
      } else {
        this.companyService
          .addList$([
            {
              title,
              description,
              type: companyType,
              unsafeSubData: {
                websiteUrl,
              },
            },
          ])
          .pipe(take(1))
          .subscribe();
      }
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }
}
